import { useState } from 'react';
import { toast } from 'sonner';

import { AutocompleteInput } from '@/components/form/AutocompleteInput';
import { cn } from '@/utils/classname';

interface LabelSectionProps<T extends string> {
  labels?: T[];
  editable: boolean;
  onChange?: (labels: T[]) => void;
  validLabels?: T[]; // Optional: provide whitelist
  validateLabel?: (label: string) => boolean; // Optional: custom validation
  placeholder?: string; // Optional: custom placeholder
  suggestedLabels?: string[]; // NEW: autocomplete suggestions
}

export function LabelSection<T extends string>({
  labels,
  editable,
  onChange,
  validLabels,
  validateLabel,
  placeholder = 'Type a label and press Enter...',
  suggestedLabels, // NEW
}: LabelSectionProps<T>) {
  const [inputValue, setInputValue] = useState('');

  const isValidLabel = (label: string): label is T => {
    if (validateLabel) {
      return validateLabel(label);
    }
    if (validLabels) {
      return validLabels.includes(label as T);
    }
    return true; // No validation specified
  };

  const handleAddLabel = (newLabel: string) => {
    if (!isValidLabel(newLabel)) {
      toast.error(`Invalid label: ${newLabel}`);
      return;
    }

    if (labels?.includes(newLabel as T)) {
      toast.error('Label already exists');
      return;
    }

    const updatedLabels = [...(labels || []), newLabel as T];
    onChange?.(updatedLabels);
    setInputValue('');
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    const updatedLabels =
      labels?.filter(label => label !== labelToRemove) || [];
    onChange?.(updatedLabels);
  };

  return (
    <div>
      <h4 className="text-chariot-text-primary mb-1 text-[10px] font-semibold">
        Labels
      </h4>

      {/* Existing labels */}
      {labels && labels.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {labels.map((label, idx) => (
            <button
              key={idx}
              onClick={() => editable && handleRemoveLabel(label)}
              disabled={!editable}
              className={cn(
                'rounded bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-medium text-blue-600',
                editable &&
                  'cursor-pointer transition-colors hover:bg-blue-500/20 hover:ring-1 hover:ring-blue-500/30'
              )}
              aria-label={editable ? `Remove ${label}` : undefined}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Input field (always visible when editable) */}
      {editable && (
        <AutocompleteInput
          value={inputValue}
          onChange={val => setInputValue(val)}
          onSubmit={handleAddLabel}
          suggestions={suggestedLabels || []}
          placeholder={placeholder}
          aria-label="Add label"
          className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary placeholder:text-chariot-text-tertiary w-full rounded border px-2 py-1 text-[9px] focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
      )}

      {/* Empty state (when not editable and no labels) */}
      {!editable && (!labels || labels.length === 0) && (
        <div className="py-2 text-center">
          <p className="text-chariot-text-secondary text-[9px]">No labels</p>
        </div>
      )}
    </div>
  );
}
